<template>
  <modalGeneric
    :title="modalConfig.label"
    @closeModal="close"
    theme="createCollection"
    v-if="showModal || !hasCoOwners">
    <div class="createCollectionModalGeneric">
      <section class="createCollectionModalGeneric__formContent">
        <div class="createCollectionModalGeneric__formContent--inputContent">
          <p class="createCollectionModalGeneric__formContent--label">NAME</p>
          <input
            type="text"
            class="createCollectionModalGeneric__formContent--nameInput bookFont"
            :class="{ input__hasError: error.name, 'disabledAction': action === 'duplicate'}"
            placeholder="Collection name"
            v-model="nameCollection">
          <span class="createCollectionModalGeneric__formContent--error" v-if="error.name">{{error.name}}</span>
        </div>
        <div
          class="createCollectionModalGeneric__formContent--selectPublic"
          :class="{'disabledAction': action === 'duplicate'}">
          <radioButtonGeneric
            class="selectPrivate"
            @update:modelValue="setModelValue()"
            label="Private"
            v-model:modelValue="isPrivateCheck">
          </radioButtonGeneric>
          <radioButtonGeneric
            class="selectPublic"
            @update:modelValue="setModelValue()"
            label="Public"
            v-model:modelValue="isPubliCheck">
          </radioButtonGeneric>
        </div>
        <div class="createCollectionModalGeneric__formContent--coOwner" v-if="isPubliCheck">
          <span class="grey-icon-add-coowner"></span>
          <span class="coOwnerTitle">Add Co-Owner</span>
          <addPeopleInputGeneric
            inputType="coOwner"
            :userLogged="userLogged"
            :validateEmails="validateEmails"
            :initialEmails="coOwnersList"
            :failedEmails="failedEmails"
            @emailsValidated="executeAction($event)"
            @listUpdated="updateCoOwners($event)"
            ref="peopleInputRef">
          </addPeopleInputGeneric>
        </div>
        <span class="createCollectionModalGeneric__formContent--error" v-if="error.service">{{error.service}}</span>
      </section>
      <section class="createCollectionModalGeneric__buttons bookFont">
        <button class="createCollectionModalGeneric__buttons--cancel" @click.stop="cancel">{{creatingWithItems ? 'BACK' : 'CANCEL'}}</button>
        <button class="createCollectionModalGeneric__buttons--create" @click.stop="actionClick" @mousedown="forceClick" :disabled="isCreatingCollection" ref="buttonActionRef">
          {{modalConfig.labelBtn}}
          <spinnerGeneric v-if="isCreatingCollection" class="createCollectionSpinner"></spinnerGeneric>
        </button>
      </section>
    </div>
  </modalGeneric>
</template>

<script>
import { reactive, ref, computed, onBeforeMount } from 'vue'
import modalGeneric from '../modalGeneric/modalGeneric'
import radioButtonGeneric from '../radioButtonGeneric/radioButtonGeneric'
import spinnerGeneric from '../spinnerGeneric/spinnerGeneric'
import addPeopleInputGeneric from '../addPeopleInputGeneric/addPeopleInputGeneric'
import messagesConfig from './messagesCreateCollection.json'
import { getCollectionCoOwners, getCollectionDuplicateInfo, createNewCollection, duplicateNewCollection, getUsersDataByEmail, editCollectionbyId } from '../../../services'

export default {
  name: 'createCollectionModalGeneric',
  components: {
    radioButtonGeneric,
    spinnerGeneric,
    modalGeneric,
    addPeopleInputGeneric
  },
  props: {
    cardsId: {
      type: Array,
      required: null
    },
    action: {
      type: String,
      default: 'create',
      validator (value) {
        return ['create', 'edit', 'duplicate'].includes(value)
      }
    },
    creatingWithItems: {
      type: Boolean,
      default: false
    },
    name: {
      type: String,
      default: null
    },
    collectionId: {
      type: String,
      default: null
    },
    isPublic: {
      type: Boolean,
      default: false
    },
    coOwners: {
      type: Number,
      default: 0
    },
    isMine: {
      type: Boolean,
      default: true
    },
    userLogged: {
      type: String,
      default: ''
    }
  },
  emits: ['cancel-action', 'reload-collections', 'close'],
  setup (props, { emit }) {
    const isPubliCheck = ref(false)
    const isPrivateCheck = ref(true)
    const nameCollection = ref('')
    const error = reactive({
      name: '',
      type: '',
      service: ''
    })
    const dataCollection = reactive({
      name: null,
      isPublic: null,
      coOwners: []
    })
    const isCreatingCollection = ref(false)
    const messages = reactive(messagesConfig)
    const validateEmails = ref(false)
    const coOwnersList = ref([])
    const coOwnersDetails = ref([])
    const showModal = ref(false)
    const peopleInputRef = ref(null)
    const failedEmails = ref([])
    const buttonActionRef = ref(null)

    /**
     * @description Return modal data from JSON
     */
    const modalConfig = computed(() => {
      return messages.modalConfig[props.action]
    })

    /**
     * @description Return if collection has co-owners
     */
    const hasCoOwners = computed(() => {
      return props.coOwners && props.action === 'edit'
    })

    onBeforeMount(() => {
      addOverflowAuto()
      if (props.action === 'edit') {
        setUpEditModal()
      } else if (props.action === 'duplicate') {
        setUpDuplicateModal()
      }
    })

    /**
     * @description Set Up initial data from modal when editing a collection
     */
    function setUpEditModal () {
      nameCollection.value = props.name || ''
      isPubliCheck.value = props.isPublic
      isPrivateCheck.value = !props.isPublic

      if (hasCoOwners.value) {
        getCollectionCoOwners(props.collectionId, props.coOwners, 1).then((response) => {
          coOwnersDetails.value = response
          generateCoOwnersList()
          showModal.value = true
        })
      }
    }

    /**
     * @description Set Up initial data from modal when duplicating a collection
     */
    function setUpDuplicateModal () {
      getCollectionDuplicateInfo(props.collectionId).then((response) => {
        nameCollection.value = response.data.name || ''
        isPubliCheck.value = false
        isPrivateCheck.value = true
      })
    }

    /**
     * @description Close create modal
     */
    function close () {
      emit('close')
      window.scrollTo(0, 0)
    }

    /**
     * @description Cancel action
     */
    function cancel () {
      emit('cancel-action')
      window.scrollTo(0, 0)
    }

    /**
     * @description Set the model value for private / public collection
     */
    function setModelValue () {
      isPubliCheck.value = !isPubliCheck.value
      isPrivateCheck.value = !isPrivateCheck.value
      coOwnersList.value = []
    }

    /**
     * @description Update collection information
     */
    function updateDataCollection () {
      dataCollection.name = nameCollection.value
      dataCollection.isPublic = isPubliCheck.value
    }

    /**
     * @description Handle click button event and call action according action
     */
    function actionClick () {
      updateDataCollection()
      isCreatingCollection.value = true
      error.name = !nameCollection.value.trim() ? 'Collection name cannot be empty' : ''
      if (nameCollection.value.trim()) {
        if ((coOwnersList.value.length && isPubliCheck.value) || (!props.isMine && isPubliCheck.value)) validateEmails.value = true
        else callCollectionAction()
      } else {
        isCreatingCollection.value = false
      }
    }

    /**
     * @description Create collection
     * @param name Name of the collection
     * @param isPublic Indicates if is public or private collection
     * @param {items} Array Of items to add
     */
    function createCollection (name, isPublic, items, coOwners) {
      const data = {
        name: name,
        isPublic: isPublic,
        items: items || [],
        coOwners: coOwners || []
      }
      createNewCollection(data).then((response) => {
        afterChangeCollections(response)
      })
    }

    /**
     * @description Edit collection
     * @param name Name of the collection
     * @param isPublic Indicates if is public or private collection
     * @param id Id of the collection to edit
     */
    function editCollection (name, isPublic, id, coOwners) {
      const data = {
        name: name,
        isPublic: isPublic,
        coOwners: coOwners || []
      }
      editCollectionbyId(data, id).then((response) => {
        afterChangeCollections(response)
      })
    }

    /**
     * @description Duplicate collection
     * @param name Name of the collection
     * @param id Id of the collection to duplicate
    */
    function duplicateCollection (name, id) {
      const data = {
        name: name,
        id: id
      }
      duplicateNewCollection(data).then(() => {
        reloadCollections()
        close()
      })
    }

    /**
     * @description Handle response and update view after collections changes.
     * @param {response} Object Response from service
    */
    function afterChangeCollections (response) {
      isCreatingCollection.value = false
      if (response.data.success) {
        if (props.creatingWithItems) {
          cancel()
        } else {
          reloadCollections()
          close()
        }
      } else {
        error.service = "Oops! Something went wrong and we couldn't process your request."
      }
    }

    /**
     * @description Request collection list again to update it
     */
    function reloadCollections () {
      emit('reload-collections')
    }

    /**
     * @description Updates list of co-owners
     * @param emails Array of emails
     */
    function updateCoOwners (emails) {
      coOwnersList.value = emails
    }

    /**
     * @description Gets array of user objects
     */
    function getUsersData () {
      let users
      if (!props.isMine) users = [...coOwnersList.value, props.userLogged]
      else users = coOwnersList.value.filter(email => !isUserLogged(email))
      if (!users.length && coOwnersList.value.length === 1 && isUserLogged(coOwnersList.value[0])) {
        callCollectionAction()
        return
      }
      getUsersDataByEmail(users).then((response) => {
        failedEmails.value = getInvalidEmails(users, response)
        if (failedEmails.value.length) {
          isCreatingCollection.value = false
          peopleInputRef.value.setError(`${failedEmails.value.length === 1 ? 'single' : 'multiple'}Error`, failedEmails.value.length === 1 ? failedEmails.value[0] : '')
        } else {
          response.forEach(user => delete user.thumbnail)
          dataCollection.coOwners = response
          callCollectionAction()
        }
      })
    }

    /**
     * @description Calls service according action
     */
    function callCollectionAction () {
      if (props.action === 'create') {
        createCollection(dataCollection.name, dataCollection.isPublic, props.cardsId, dataCollection.coOwners)
      } else if ((props.action === 'edit')) {
        editCollection(dataCollection.name, dataCollection.isPublic, props.collectionId, dataCollection.coOwners)
      } else if ((props.action === 'duplicate')) {
        duplicateCollection(dataCollection.name, props.collectionId)
      }
    }

    /**
     * @description Executes action when emails validation are successfull
     */
    function executeAction (validation) {
      validateEmails.value = false
      if (validation === 'success') getUsersData()
      else isCreatingCollection.value = false
    }

    /**
     * @description Generates a list of co-owners emails excluding user logged
     */
    function generateCoOwnersList () {
      coOwnersDetails.value.forEach(({ email }) => {
        if (!isUserLogged(email)) coOwnersList.value.push(email)
      })
    }

    /**
     * @description Gets an array of emails that aren't on response
     * @param emailsSent A list of emails sent to service
     * @param response A list of valid users got from service
     */
    function getInvalidEmails (emailsSent, response) {
      return emailsSent.filter(email => {
        return !response.some(responseUser => responseUser.email === email.toLowerCase())
      })
    }

    /**
     * @description Force click event before blur event is fired
     */
    function forceClick () {
      setTimeout(() => {
        buttonActionRef.value.click()
      }, 0)
    }

    /**
     * @description Adds overflow auto to html when user agent is iphone
     */
    function addOverflowAuto () {
      if (navigator.userAgent.includes('iphone')) {
        document.documentElement.style.overflowY = 'auto'
      }
    }

    /**
     * @description Check if email correspond to the user looged email
     * @param email email to check
     */
    function isUserLogged (email) {
      return props.userLogged === email.toLowerCase()
    }

    return {
      actionClick,
      isPubliCheck,
      isPrivateCheck,
      nameCollection,
      error,
      close,
      isCreatingCollection,
      setModelValue,
      messages,
      modalConfig,
      cancel,
      validateEmails,
      coOwnersList,
      updateCoOwners,
      executeAction,
      showModal,
      hasCoOwners,
      peopleInputRef,
      failedEmails,
      forceClick,
      buttonActionRef
    }
  }
}
</script>
